import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import LongTextWithTooltip from 'lib/designSystem/LongTextWithTooltip';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import React from 'react';

import { formatMoney } from '../../../../../../../../../lib/functions/utils';
import {
  AccountingEntryLine,
  AccountingNode,
  CostCenter,
} from '../../../../../types';

interface EntryLinesDetailClasses {
  tableCell?: string;
}

interface EntryLinesDetailProps {
  lines: AccountingEntryLine[];
  classes?: EntryLinesDetailClasses;
  nodes?: AccountingNode[];
  costCenters?: CostCenter[];
}

const EntryLinesDetail: React.FC<EntryLinesDetailProps> = ({
  lines,
  classes,
  nodes,
  costCenters,
}) => {
  const totalDebit = lines.reduce((sum, line) => sum + line.debit, 0);
  const totalCredit = lines.reduce((sum, line) => sum + line.credit, 0);

  const getNodeKey = (line: AccountingEntryLine): string => {
    if (line.node?.key) {
      return line.node.key;
    }
    if (line.nodeId && nodes) {
      const node = nodes.find((n) => n.id === line.nodeId);
      return node?.key || '-';
    }
    return '-';
  };
  const getNodeAccountCode = (line: AccountingEntryLine) => {
    if (line.node?.accountCode) {
      return line.node.accountCode;
    }
    if (line.nodeId && nodes) {
      const node = nodes.find((n) => n.id === line.nodeId);
      return node?.accountCode || '-';
    }
  };

  const getCostCenterName = (
    costCenterId: number | null | undefined,
  ): string => {
    if (!costCenterId) return '-';
    if (costCenters) {
      const costCenter = costCenters.find((cc) => cc.id === costCenterId);
      return costCenter?.name || String(costCenterId);
    }
    return String(costCenterId);
  };

  const longText = (text: string) => {
    return (
      <LightTooltip
        placement="top"
        title={<Typography style={{ padding: 4 }}>{text}</Typography>}
      >
        <div>{text}</div>
      </LightTooltip>
    );
  };

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell style={{ fontWeight: 'bold' }}>N° Cuenta</TableCell>
          <TableCell style={{ fontWeight: 'bold' }}>Cuenta</TableCell>
          <TableCell style={{ textAlign: 'right', fontWeight: 'bold' }}>
            Debe
          </TableCell>
          <TableCell style={{ textAlign: 'right', fontWeight: 'bold' }}>
            Haber
          </TableCell>
          <TableCell style={{ fontWeight: 'bold' }}>Glosa</TableCell>
          <TableCell style={{ fontWeight: 'bold' }}>Rut</TableCell>
          <TableCell style={{ fontWeight: 'bold' }}>C.Costos</TableCell>
          <TableCell style={{ fontWeight: 'bold' }}>N° Docto.</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {lines.map((line, index) => (
          <TableRow key={index}>
            <TableCell
              style={{ textAlign: 'left' }}
              className={classes?.tableCell}
            >
              {getNodeAccountCode(line)}
            </TableCell>
            <TableCell
              style={{ textAlign: 'left' }}
              className={classes?.tableCell}
            >
              {longText(getNodeKey(line))}
            </TableCell>
            <TableCell
              style={{ textAlign: 'right' }}
              className={classes?.tableCell}
            >
              {formatMoney(line.debit)}
            </TableCell>
            <TableCell
              style={{ textAlign: 'right' }}
              className={classes?.tableCell}
            >
              {formatMoney(line.credit)}
            </TableCell>
            <TableCell className={classes?.tableCell}>
              {longText(line.description || '-')}
            </TableCell>
            <TableCell className={classes?.tableCell}>
              {line.businessIdentifier || '-'}
            </TableCell>
            <TableCell className={classes?.tableCell}>
              {getCostCenterName(line.costCenterId)}
            </TableCell>

            <TableCell className={classes?.tableCell}>
              {line.documentKey || '-'}
            </TableCell>
          </TableRow>
        ))}
        <TableRow>
          <TableCell
            style={{
              fontWeight: 'bold',
              textAlign: 'left',
            }}
            className={classes?.tableCell}
            colSpan={2}
          >
            Totales:
          </TableCell>
          <TableCell
            style={{
              fontWeight: 'bold',
              textAlign: 'right',
            }}
            className={classes?.tableCell}
          >
            {formatMoney(totalDebit)}
          </TableCell>
          <TableCell
            style={{
              fontWeight: 'bold',
              textAlign: 'right',
            }}
            className={classes?.tableCell}
          >
            {formatMoney(totalCredit)}
          </TableCell>
          <TableCell colSpan={4} className={classes?.tableCell}></TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};

export default EntryLinesDetail;
